// src/design-system/components/richTextEditor/LinkModal.tsx
import { useState, useEffect, useMemo } from "react";
import { Link2 } from "lucide-react";
import * as yup from "yup";
import { Modal } from "../modal/modal";
import { Input } from "../input/Input";
import { Checkbox } from "../checkbox/Checkbox";
import { colors } from "../../theme/colors";
import type { LinkModalProps } from "./types";

// URL validation schema
const linkSchema = yup.object().shape({
  url: yup
    .string()
    .required("URL is required")
    .test("has-content", "URL is required", (value) => {
      return !!value?.trim();
    })
    .test("valid-url", "Please enter a valid URL", (value) => {
      if (!value?.trim()) return false;

      let urlToTest = value.trim();

      // Add protocol if missing
      if (!/^https?:\/\//i.test(urlToTest)) {
        urlToTest = `https://${urlToTest}`;
      }

      try {
        const url = new URL(urlToTest);
        // Must be http or https
        if (!["http:", "https:"].includes(url.protocol)) {
          return false;
        }
        // Must have a valid hostname
        if (!url.hostname || url.hostname.length < 1) {
          return false;
        }
        // Basic domain validation (must have at least one dot for TLD, unless localhost)
        if (url.hostname !== "localhost" && !url.hostname.includes(".")) {
          return false;
        }
        return true;
      } catch {
        return false;
      }
    })
    .test("no-spaces", "URL cannot contain spaces", (value) => {
      if (!value) return true;
      return !value.includes(" ");
    })
    .test("valid-characters", "URL contains invalid characters", (value) => {
      if (!value?.trim()) return true;
      // Check for obviously invalid characters
      const invalidChars = /[<>{}|\\\^`\[\]]/;
      return !invalidChars.test(value);
    }),
});

// Helper to ensure URL has protocol
const ensureProtocol = (url: string): string => {
  const trimmedUrl = url.trim();
  if (!/^https?:\/\//i.test(trimmedUrl)) {
    return `https://${trimmedUrl}`;
  }
  return trimmedUrl;
};

export const LinkModal = ({
  open,
  onClose,
  onConfirm,
  initialUrl = "",
  initialNewTab = true,
}: LinkModalProps) => {
  const [url, setUrl] = useState(initialUrl);
  const [openInNewTab, setOpenInNewTab] = useState(initialNewTab);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setOpenInNewTab(initialNewTab);
      setError(null);
      setTouched(false);
    }
  }, [open, initialUrl, initialNewTab]);

  // Validate URL and determine if form is valid
  const isValid = useMemo(() => {
    try {
      linkSchema.validateSync({ url });
      return true;
    } catch {
      return false;
    }
  }, [url]);

  // Validate on change and show error only if touched
  const validateUrl = (value: string) => {
    try {
      linkSchema.validateSync({ url: value });
      setError(null);
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setError(err.message);
      }
    }
  };

  const handleUrlChange = (value: string) => {
    setUrl(value);
    if (touched) {
      validateUrl(value);
    }
  };

  const handleUrlBlur = () => {
    setTouched(true);
    validateUrl(url);
  };

  const handleOk = async () => {
    setTouched(true);

    try {
      linkSchema.validateSync({ url });
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        setError(err.message);
      }
      return;
    }

    const processedUrl = ensureProtocol(url);

    setLoading(true);
    try {
      onConfirm(processedUrl, openInNewTab);
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUrl("");
    setOpenInNewTab(true);
    setError(null);
    setTouched(false);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && isValid) {
      e.preventDefault();
      handleOk();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={initialUrl ? "Edit Link" : "Insert Link"}
      icon={<Link2 size={20} />}
      size="medium"
      onOk={handleOk}
      onCancel={handleClose}
      okText={initialUrl ? "Update" : "Insert"}
      cancelText="Cancel"
      confirmLoading={loading}
      okButtonProps={{ disabled: !isValid }}
    >
      <div className="p-6 space-y-4">
        <Input
          label="URL"
          required
          placeholder="https://example.com"
          value={url}
          onChange={(e) => handleUrlChange(e.target.value)}
          onBlur={handleUrlBlur}
          onKeyDown={handleKeyDown}
          error={touched && error ? error : undefined}
          prefix={<Link2 size={16} style={{ color: colors.textMuted }} />}
          autoFocus
          variant="filled"
        />

        <Checkbox
          checked={openInNewTab}
          onChange={(e) => setOpenInNewTab(e.target.checked)}
          label="Open in new tab"
          size="medium"
        />
      </div>
    </Modal>
  );
};

export default LinkModal;
